import {
  fetchSupportGuideline,
  fetchTicketCommentCount,
  useSupportStore,
  supportNavigationState,
} from '@/entities/support';
import { useUserStore } from '@/entities/user';
import {
  BIOTRICITY_SUPPORT_ADDRESS,
  HEART_SMART_SUPPORT_ADDRESS,
  ORGANIZATION_SLUG,
} from '@/shared/config';
import { auth, showMessageError, GETTEXT_ALL_FACILITIES, handleFetchCountries } from '@/shared/lib';
import { useMergeState, useUpdateEffect, useElementChange } from '@/shared/lib/customHooks';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';

/**
 * Backs the support page: facility scope, tabs, header scroll state and the feedback modal. Resolves the support email from the organization slug, loads comment count, guideline and countries on mount, tracks window scroll and the login banner class on the header ref, refetches the count when the selected facility changes, and exposes tab navigation and the modal toggle.
 *
 * @returns Facility data, support email, header ref, page state and the tab and modal handlers.
 */
export const useSupport = () => {
  /**
   * Holds the support facilities available to the user, read from the support store. Subscribes the support page to the `supportFacilities` slice so the list refreshes whenever that store entry changes.
   *
   * @returns The support facility list held in the support store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * Holds the facility currently selected by the user, read from the user store. Subscribes the support page to the `selectedFacility` slice so the value refreshes whenever that store entry changes.
   *
   * @returns The selected facility object held in the user store.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);
  const organization = auth.userOrganization() || {};
  const isHeartSmart = organization.slug === ORGANIZATION_SLUG.HEART_SMART;
  const supportEmail = isHeartSmart
    ? HEART_SMART_SUPPORT_ADDRESS.email
    : BIOTRICITY_SUPPORT_ADDRESS.csMail;

  const headerRef = React.useRef<any>(null);

  const isChangePosition = useElementChange(headerRef);

  const [state, setState] = useMergeState<any>({
    activeTab: supportNavigationState.tab || '1',
    supportGuideline: {},
    countries: [],
    ticketCommentCount: 0,
    isOpenSendFeedbackModal: false,
  });

  /**
   * Loads the support page's unread comment count and the support guideline for the selected facility. Builds a Clinic ticket filter scoped to the chosen facility, unscoped when all facilities are selected, returns after only the comment count when the flag is set, otherwise fetches count and guideline in parallel, and logs plus surfaces any error.
   *
   * @param isFetchCommentCountOnly - True to refresh only the new-comment count and skip the guideline.
   * @returns Resolves after the fetched values are stored.
   */
  const fetchData = async (isFetchCommentCountOnly = false) => {
    try {
      const ticketCommentCountFilter = {
        ticketType: 'Clinic',
        isNewComment: true,
        facility:
          selectedFacilityRedux.value === GETTEXT_ALL_FACILITIES
            ? undefined
            : selectedFacilityRedux.value,
      };
      if (isFetchCommentCountOnly) {
        const ticketCommentCount = await fetchTicketCommentCount(ticketCommentCountFilter);
        setState({ ticketCommentCount });
        return;
      }
      const promise = [fetchTicketCommentCount(ticketCommentCountFilter), fetchSupportGuideline()];
      const [ticketCommentCount, supportGuideline] = await Promise.all(promise);
      setState({ supportGuideline, ticketCommentCount });
    } catch (error) {
      console.error('Failed to fetch data: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Remembers which support tab the user is on so it survives navigation away and back. Assigns the tab onto the shared support navigation state object.
   *
   * @param tab - Identifier of the tab to remember.
   * @returns Nothing.
   */
  const updateTabGoto = (tab: any) => {
    _.assign(supportNavigationState, { tab });
  };

  /**
   * Switches the support page to another tab. Does nothing when the tab is already active, otherwise records it in the support navigation state and stores it as the active tab.
   *
   * @param tab - Identifier of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      updateTabGoto(tab);
      setState({ activeTab: tab });
    }
  };

  /**
   * Opens or closes the send feedback modal of the support page. Inverts the isOpenSendFeedbackModal flag in hook state.
   *
   * @returns Nothing.
   */
  const toggleSendFeedbackModal = () => {
    setState({ isOpenSendFeedbackModal: !state.isOpenSendFeedbackModal });
  };

  /**
   * Toggles the scrolled styling of the support page header. Adds the is-scrolling class to the header ref while the window is scrolled away from the top and removes it once back at the top.
   *
   * @returns Nothing.
   */
  const handleScroll = () => {
    if (headerRef.current) {
      const { classList } = headerRef.current;
      if (window.scrollY === 0 && classList.contains('is-scrolling')) {
        headerRef.current.classList.remove('is-scrolling');
      } else if (window.scrollY !== 0 && !classList.contains('is-scrolling')) {
        headerRef.current.classList.add('is-scrolling');
      }
    }
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Support`;
    if (!supportNavigationState.tab) {
      updateTabGoto('1');
    }
    /**
     * Fills the support page's country list used by its feedback and request forms. Awaits the countries fetch and stores the returned list in state.
     *
     * @returns Resolves once the country list is stored.
     */
    const loadCountries = async () => {
      const countriesList = await handleFetchCountries();
      setState({ countries: countriesList });
    };
    fetchData();
    loadCountries();
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useUpdateEffect(() => {
    if (
      (supportFacilitiesRedux?.length ?? 0) > 0 ||
      facilitySelection.userSupportFacilities()?.length > 0
    ) {
      fetchData(true);
    }
  }, [selectedFacilityRedux.value]);

  return {
    supportFacilitiesRedux,
    selectedFacilityRedux,
    supportEmail,
    headerRef,
    state,
    navigateTab,
    toggleSendFeedbackModal,
  };
};
